import React from 'react';
import Contract from '../../../../../../../../../models/contract';
import { getVehicleInfo } from '../../../../../../../../../models/helpers/form';
import { Form } from 'react-bootstrap';

interface VehicleInfoProps {
  contract: Contract | null;
  className?: string;
  currentKilometrage?: number;
  onKilometrageChange?: (value: number) => void;
}

/**
 * Composant qui affiche les informations du véhicule
 * Utilisé dans l'étape de qualification pour évaluer l'éligibilité du dossier
 */
const VehicleInfo: React.FC<VehicleInfoProps> = ({
  contract,
  className = '',
  currentKilometrage,
  onKilometrageChange
}) => {
  if (!contract) {
    return (
      <div className={`tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-border tw-border-gray-200 ${className}`}>
        <div className="tw-p-3 tw-text-center">
          <h3 className="tw-text-lg tw-font-medium tw-text-gray-600">Informations véhicule</h3>
          <p className="tw-text-gray-500">Aucune information de véhicule disponible</p>
        </div>
      </div>
    );
  }

  // Récupération des informations du véhicule
  const vehicleInfo = getVehicleInfo(contract);
  const kmStart = vehicleInfo.kmStart ? Number(vehicleInfo.kmStart) : 0;
  const kmLimit = contract.warrantyKmLimit || (contract.warranty?.kmLimit || 0);

  // Calcul du kilométrage maximum autorisé
  const maxAllowedKm = kmStart + kmLimit;

  // Vérification du dépassement de kilométrage
  const isKmExceeded = currentKilometrage !== undefined && maxAllowedKm > 0 && currentKilometrage > maxAllowedKm;

  // Gestion du changement de kilométrage
  const handleKilometrageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && onKilometrageChange) {
      onKilometrageChange(value);
    }
  };

  return (
    <div className={`tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm ${className}`}>
      <div className="tw-p-4 tw-border-b tw-border-gray-200">
        <h3 className="tw-text-lg tw-font-medium tw-flex tw-items-center tw-gap-2">
          <i className="fa-solid fa-car tw-text-blue-600"></i>
          Informations véhicule
        </h3>
      </div>

      <div className="tw-p-4">
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
          {/* Caractéristiques du véhicule */}
          <div>
            <h4 className="tw-text-sm tw-uppercase tw-text-gray-500 tw-mb-2">Caractéristiques</h4>
            <dl className="tw-space-y-2">
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Marque</dt>
                <dd className="tw-font-medium tw-text-gray-800">{vehicleInfo.brand || '-'}</dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Modèle</dt>
                <dd className="tw-font-medium tw-text-gray-800">{vehicleInfo.model || '-'}</dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Immatriculation</dt>
                <dd className="tw-font-medium tw-text-gray-800">{vehicleInfo.registration || '-'}</dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Puissance fiscale</dt>
                <dd className="tw-font-medium tw-text-gray-800">{vehicleInfo.fiscalPower || '-'}</dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Mise en circulation</dt>
                <dd className="tw-font-medium tw-text-gray-800">{vehicleInfo.entryIntoService || '-'}</dd>
              </div>
            </dl>
          </div>

          {/* Informations de kilométrage */}
          <div>
            <h4 className="tw-text-sm tw-uppercase tw-text-gray-500 tw-mb-2">Kilométrage</h4>
            <dl className="tw-space-y-2">
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Kilométrage initial</dt>
                <dd className="tw-font-medium tw-text-gray-800">
                  {kmStart ? `${kmStart.toLocaleString('fr-FR')} km` : '-'}
                </dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Limite contractuelle</dt>
                <dd className="tw-font-medium tw-text-gray-800">
                  {kmLimit ? `${kmLimit.toLocaleString('fr-FR')} km` : '-'}
                </dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-text-gray-600">Maximum autorisé</dt>
                <dd className="tw-font-medium tw-text-gray-800">
                  {maxAllowedKm ? `${maxAllowedKm.toLocaleString('fr-FR')} km` : '-'}
                </dd>
              </div>
            </dl>

            {/* Saisie du kilométrage actuel */}
            <div className="tw-mt-4">
              <label htmlFor="currentKilometrage" className="tw-block tw-text-sm tw-text-gray-600 tw-mb-1">
                Kilométrage actuel
              </label>
              <div className="tw-flex tw-items-center">
                <Form.Control
                  id="currentKilometrage"
                  type="number"
                  min="0"
                  value={currentKilometrage || ''}
                  onChange={handleKilometrageChange}
                  className={`tw-border ${isKmExceeded ? 'tw-border-red-300' : 'tw-border-gray-300'} tw-rounded-md tw-py-1 tw-px-3`}
                  placeholder="Saisir le kilométrage actuel"
                />
                <span className="tw-ml-2 tw-text-gray-600">km</span>
              </div>

              {/* Alerte en cas de dépassement */}
              {isKmExceeded && (
                <div className="tw-mt-2 tw-text-sm tw-text-red-600">
                  <i className="fa-solid fa-triangle-exclamation tw-mr-1"></i>
                  Kilométrage supérieur à la limite contractuelle
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Info-bulle d'aide */}
        <div className="tw-mt-4 tw-bg-blue-50 tw-p-3 tw-rounded-lg tw-text-sm tw-text-blue-700 tw-flex tw-items-start tw-gap-2">
          <div className="tw-text-blue-600 tw-mt-0.5">
            <i className="fa-solid fa-circle-info"></i>
          </div>
          <div>
            <p>Le kilométrage actuel du véhicule est un critère déterminant pour la prise en charge sous garantie.</p>
            <p className="tw-mt-1">Assurez-vous de saisir le kilométrage exact au moment de la panne.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleInfo;